import { formatDate } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  inject,
  input,
  LOCALE_ID,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { Chart } from 'chart.js';
import 'chartjs-adapter-date-fns';
import zoomPlugin from 'chartjs-plugin-zoom';
import { ButtonModule } from 'primeng/button';
import { ChartModule, UIChart } from 'primeng/chart';
import { PanelModule } from 'primeng/panel';
import { interval, Subject, takeUntil } from 'rxjs';
import { IEnergyMeterLog } from '../../../../../models/energy-meter';
import { EnergyMeter } from '../../../../../services/energy-meter';
import { Functions } from '../../../../../services/shared/functions';
import {
  CommandLogData,
  CommandType,
  SocketService,
} from '../../../../../services/socket';

Chart.register(zoomPlugin);

@Component({
  selector: 'app-graph-view',
  imports: [ChartModule, PanelModule, ButtonModule],
  templateUrl: './graph-view.html',
  styleUrl: './graph-view.css',
})
export class GraphView implements OnInit, OnDestroy, AfterViewInit {
  readonly energyMeterService = inject(EnergyMeter);
  readonly socketService = inject(SocketService);
  readonly customFunctions: Functions = inject(Functions);
  energyMeterLogInformation = input.required<IEnergyMeterLog[]>();
  data: any;
  options: any;
  platformId = inject(PLATFORM_ID);
  readonly locale = inject(LOCALE_ID);
  zoomEnabled = false;
  @ViewChild('chart') chartComponent!: UIChart;
  intervalTimer: number = 0;
  private lastEmInfo: CommandLogData[] = [];
  private destroy$ = new Subject<void>();

  constructor(private cd: ChangeDetectorRef) {}
  ngAfterViewInit(): void {
    setTimeout(() => {
      this.chartComponent.chart.update();
      this.cd.markForCheck();
    }, 2000);
  }

  ngOnInit() {
    this.initChart();
    this.createIntervalTimer();
    this.createListeners();
  }

  initChart() {
    const datasets: any[] = [];
    this.energyMeterLogInformation().forEach((info) => {
      const allDates = this.create1HArray(info.data);
      this.lastEmInfo.push({
        assetId: info.energyMeterId,
        lastLogId: info.lastEMLogId,
      });
      datasets.push({
        label: info.name,
        data: allDates,
        fill: true,
        tension: 0,
        emId: info.energyMeterId,
      });
    });

    this.data = {
      datasets: datasets,
    };

    this.options = this.buildOptions(this.zoomEnabled);
    this.cd.markForCheck();
  }

  buildOptions(enableZoom: boolean) {
    return {
      maintainAspectRatio: false,
      aspectRatio: 0.6,
      responsive: true,
      interaction: {
        intersect: false,
        mode: 'index',
      },
      scales: {
        x: {
          time: {
            tooltipFormat: 'dd/MM/yyyy HH:mm',
            unit: 'minute',
            round: 'minute',
          },
          type: 'time',
          ticks: {
            source: 'data',
            autoSkip: false,
            callback: (val: number, index: any, ticks: any) => {
              const date = new Date(val as number);
              const minutes = date.getMinutes();
              if (minutes % 15 === 0) {
                return formatDate(date, 'HH:mm', this.locale);
              }
              return '';
            },
          },
          afterBuildTicks: (scale: any) => {
            scale.ticks = scale.ticks.filter((tick: any) => {
              const date = new Date(tick.value);
              return date.getMinutes() % 15 === 0;
            });
          },
        },
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: 'Power (kW)',
          },
          afterDataLimits(scale: any) {
            const maxAbs = Math.max(
              Math.abs(scale.min ?? 0),
              Math.abs(scale.max ?? 0),
            );
            scale.min = -maxAbs;
            scale.max = maxAbs;
          },
        },
      },
      plugins: {
        zoom: {
          pan: { enabled: enableZoom, mode: 'x' },
          zoom: {
            wheel: { enabled: enableZoom },
            pinch: { enabled: enableZoom },
            mode: 'x',
            animation: {
              duration: 1000,
              easing: 'easeOutCubic',
            },
          },
        },
      },
    };
  }

  resetZoom() {
    const chart: Chart | null = this.chartComponent.chart;
    if (chart) {
      chart.resetZoom();
    }
  }

  toggleZoom() {
    this.zoomEnabled = !this.zoomEnabled;
    this.options = this.buildOptions(this.zoomEnabled);

    const chart: Chart | null = this.chartComponent?.chart;
    if (chart) {
      chart.options = this.options;
      chart.update();
    }
  }

  createIntervalTimer() {
    interval(5000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.socketService.emitRequestMessageLog(
          CommandType.get_energy_meter_log,
          this.lastEmInfo,
        );

        // this.data.datasets.forEach((elem: any, i: number) => {
        //   const lastKnownDate = new Date(elem.data.at(-1).x);
        // if (new Date().getMinutes() - lastKnownDate.getMinutes() >= 2) {
        //   this.data.datasets[i].data.push({
        //     x: new Date(lastKnownDate.getMinutes() + 1),
        //     y: NaN,
        //   });

        //   console.log(new Date().getMinutes() - lastKnownDate.getMinutes());

        //   if (this.data.datasets[i].data.length > 59) {
        //     this.data.datasets[i].data.shift();
        //   }
        // }
        //   });
      });
  }

  createListeners() {
    this.socketService
      .listen<any>(CommandType.get_energy_meter_log)
      .pipe(takeUntil(this.destroy$))
      .subscribe((msg) => {
        msg.forEach((em: any) => {
          const datasetIndex = this.data.datasets.findIndex(
            (set: any) => set.emId === em.energyMeterId,
          );

          if (datasetIndex >= 0) {
            this.data.datasets[datasetIndex].data.push({
              x: em.data[0].x,
              y: em.data[0].y,
            });
            if (this.data.datasets[datasetIndex].data.length > 59) {
              this.data.datasets[datasetIndex].data.shift();
            }
            this.lastEmInfo[
              this.lastEmInfo.findIndex(
                (emLocal) => emLocal.assetId === em.energyMeterId,
              )
            ].lastLogId = em.lastEMLogId;
          }
        });

        this.chartComponent.chart.update();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private create1HArray(data: any[]): any[] {
    const allDates = this.customFunctions.generateTimestampsForLastHour();

    allDates.forEach((date, i) => {
      data.find((o) => {
        if (new Date(o.x).getMinutes() === new Date(date.x).getMinutes()) {
          allDates[i] = o;
          return true;
        }
        return false;
      });
    });

    return allDates;
  }
}
